<template>
  <div>
    <div class="row q-gutter-md q-mb-sm">
      <model-form-input
        field="config.giftCardWidgetTitle"
        value-type="String"
        label="Title"
        :disable="!section.config.showGiftCardWidgetTitle"
        :rules="[val => val !== null || 'Field is required']"
        class="col"
      />
      <model-form-toggle
        field="config.showGiftCardWidgetTitle"
        value-type="Boolean"
        class="col-3"
        label="Show Title"
        size="lg"
      />
    </div>
    <div class="row q-gutter-md q-mb-sm">
      <div class="col">
        <model-form-input
          field="config.description"
          value-type="String"
          label="Description"
          type="textarea"
        />
      </div>
      <div class="col-3 column">
        <model-form-toggle
          field="config.showGiftCardWidgetCart"
          value-type="Boolean"
          class="col"
          label="Show Cart"
          size="lg"
        />
        <model-form-toggle
          field="config.giftCardSalesShowImage"
          value-type="Boolean"
          class="col"
          label="Show Gift Card Image"
          bottom-slots
          size="lg"
        />
        <model-form-toggle
          field="config.giftCardSalesShowName"
          value-type="Boolean"
          class="col"
          label="Show Gift Card Name"
          bottom-slots
          size="lg"
        />
      </div>
    </div>
    <div class="row q-col-gutter-sm">
      <model-form-select
        field="config.giftCardSalesClasses"
        class="col-9"
        label="Gift Card Classes"
        value-type="[String]"
        map-options
        emit-value
        multiple
        use-chips
        use-input
        new-value-mode="add-unique"
        bottom-slots
        @keyup.enter.stop
      >
        <q-tooltip>
          Use this for add gift card sales filters in the widget.
        </q-tooltip>
      </model-form-select>
      <model-form-select
        field="discountId"
        class="col-3"
        label="Bonus Gift Card Discount"
        :options="giftCardDiscounts"
        option-label="name"
        option-value="id"
        map-options
        emit-value
        clearable
        bottom-slots
      >
        <q-tooltip
          anchor="top middle"
          self="bottom middle"
        >
          Forcibly applied to any bonus gift cards. Must be available online and meet any configured restrictions.
        </q-tooltip>
      </model-form-select>
    </div>
    <div class="row q-col-gutter-md q-mb-md items-start">
      <color-selection
        class="col"
        :color-name="section.config.giftCardWidgetBackgroundColor"
        :custom-color-hex="section.config.giftCardWidgetBackgroundColorHex"
        label="Gift Cards Background Color"
        @update:model-value="setBackgroundColor"
      />
      <color-selection
        class="col"
        :color-name="section.config.giftCardWidgetTextColor"
        :custom-color-hex="section.config.giftCardWidgetTextColorHex"
        label="Gift Cards Text Color"
        @update:model-value="setTextColor"
      />
    </div>
    <div class="row q-col-gutter-md q-mb-md items-start">
      <color-selection
        class="col-6"
        :color-name="section.config.blockBackgroundColor"
        :custom-color-hex="section.config.blockBackgroundColorHex"
        label="Widget Background Color"
        @update:model-value="setColor"
      />
      <model-form-select
        field="config.blockPosition"
        value-type="String"
        class="col-3"
        label="Widget Position"
        :options="$mh.asOptions(TEXT_POSITION_TYPES)"
        map-options
        emit-value
      />
      <div
        class="col-6 col-sm-3"
      >
        <q-item-label>Border Radius</q-item-label>
        <q-slider
          v-model="section.config.borderRadius"
          label
          label-always
          switch-label-side
          type="number"
          :min="0"
          :max="32"
          :step="4"
        />
        <q-tooltip>
          Border radius will apply to card and button.
        </q-tooltip>
      </div>
    </div>
    <div class="row q-gutter-md q-mb-md">
      <q-table
        v-model:pagination="pagination"
        class="col"
        :columns="giftCardSalesColumns"
        :rows="section.config.giftCardSales"
        :rows-per-page-options="[0]"
        hide-pagination
        bordered
        flat
        wrap-cells
        row-key="key"
        @row-click="(_event, row) => editGiftCardSale(row)"
      >
        <template #top>
          <div class="q-table__title">
            Gift Card Sales
          </div>

          <q-space />

          <q-btn
            class="q-mx-sm"
            label="Set Order"
            @click="$refs.orderDialog.show()"
          />
          <q-btn
            label="Add Gift Card Sale"
            outline
            @click="editGiftCardSale(null)"
          />
        </template>

        <template #body-cell="props">
          <q-td
            :props="props"
          >
            <template v-if="props.col.component === 'checkbox'">
              <q-checkbox
                disable
                :model-value="props.value"
              />
            </template>
            <template v-else-if="props.col.component === 'img'">
              <img
                v-if="props.value"
                height="60px"
                :src="props.value"
              >
            </template>
            <template v-else>
              {{ props.value }}
            </template>
          </q-td>
        </template>

        <template
          #body-cell-actions="props"
        >
          <q-td
            :props="props"
          >
            <slot
              name="row-actions"
              v-bind="props"
            >
              <div class="row q-gutter-sm justify-end no-wrap">
                <slot
                  name="prepend-row-actions"
                  v-bind="props"
                />
                <q-btn
                  :hover="false"
                  label="Edit"
                  @click="editGiftCardSale(props.row)"
                />
                <q-btn
                  :hover="false"
                  icon="more_vert"
                  round
                  @click.stop
                >
                  <q-menu>
                    <q-list style="min-width: 180px">
                      <q-item
                        v-close-popup
                        clickable
                        @click="editGiftCardSale(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="edit" />
                        </q-item-section>
                        <q-item-section>
                          Edit
                        </q-item-section>
                      </q-item>
                      <q-separator />
                      <q-item
                        v-close-popup
                        clickable
                        @click="deleteGiftCardSale(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="delete" />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('actions.remove') }}
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
            </slot>
          </q-td>
        </template>
      </q-table>
    </div>
    <preview
      :section="section"
      class="q-mb-xl"
    />

    <set-display-order-dialog
      ref="orderDialog"
      :list="section.config.giftCardSales"
      @reorder="handleReorder"
    />
    <gift-card-sale-form
      ref="giftCardSaleForm"
      :section="section"
      @save="giftCardSale => saveGiftCardSale(giftCardSale)"
    />
  </div>
</template>

<script>
import { giftCardSaleImageUrl, TYPES as GIFT_CARD_SALE_TYPES } from 'src/models/gift-card/GiftCardSale'
import { LocalizedJsonObject } from 'src/models/model'
import CheckoutData from 'src/models/order/CheckoutData'
import Order from 'src/models/order/Order'
import Section, { TEXT_POSITION_TYPES } from 'src/models/page/Section'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import GiftCardSaleForm from 'src/modules/admin/section/GiftCardSaleForm.vue'
import Preview from 'src/modules/consumer/components/Preview'

export default {
  name: 'GiftCardSalesWidgetEditor',
  components: {
    Preview,
    GiftCardSaleForm,
    SetDisplayOrderDialog,
    ColorSelection
  },
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  configuration: ['currentCircuit', 'currentSite', 'discounts'],
  data () {
    return {
      checkoutData: new CheckoutData(),
      addNewOptionError: null,
      order: new Order({}, {
        orderItems: {
          itemOrderItem: {
            item: true,
            itemVariation: true,
            itemModifiers: {
              modifier: true
            }
          }
        }
      }),
      giftCardSalesColumns: [
        { name: 'name', align: 'left', label: 'Name', field: (giftCardSale) => giftCardSale[this.localizeKey('name')] || giftCardSale.name, sortable: true },
        { name: 'type', align: 'left', label: 'Type', field: (giftCardSale) => _.startCase(giftCardSale.type), sortable: true },
        { name: 'class', align: 'left', label: 'Class', field: 'class', sortable: true },
        { name: 'amount', align: 'left', label: 'Amount', field: (giftCardSale) => this.formatAmount(giftCardSale), sortable: true },
        { name: 'image', align: 'left', label: 'Image', field: (giftCardSale) => this.formatImage(giftCardSale), component: 'img' },
        { name: 'actions', label: 'Actions' }
      ],
      pagination: {
        page: 1,
        rowsPerPage: 0
      }
    }
  },
  computed: {
    giftCardDiscounts () {
      return _.filter(this.discounts.data, { type: 'gift-card', availableOnline: true })
    }
  },
  created () {
    _.extend(this, { TEXT_POSITION_TYPES })
  },
  unmounted () {
    this.order?.destroy()
    this.checkoutData?.destroy()
  },
  methods: {
    saveGiftCardSale (giftCardSale) {
      const index = _.findIndex(this.section.config.giftCardSales, { key: giftCardSale.key })
      if (index === -1) {
        this.section.config.giftCardSales.push(giftCardSale)
      } else {
        this.section.config.giftCardSales[index] = giftCardSale
      }
    },
    editGiftCardSale (giftCardSale = null) {
      const index = giftCardSale ? _.findIndex(this.section.config.giftCardSales, { key: giftCardSale.key }) : null
      this.$refs.giftCardSaleForm.show(index)
    },
    deleteGiftCardSale (giftCardSale) {
      const index = _.findIndex(this.section.config.giftCardSales, { key: giftCardSale.key })
      this.section.config.giftCardSales.splice(index, 1)
    },
    handleReorder ({ reorderedList }) {
      this.section.config.giftCardSales = reorderedList
    },
    localizeKey (key) {
      const localizedKey = LocalizedJsonObject.localizeKey(key, this.section.language)[0]
      return localizedKey
    },
    formatAmount (giftCardSale) {
      if (giftCardSale.type === GIFT_CARD_SALE_TYPES.CUSTOM) {
        const customMinimumAmount = `${this.currentCircuit.localeCurrencySymbol}${_.toNumber(giftCardSale.customMinimumAmount).toFixed(2)}`
        const customMaximumAmount = `${this.currentCircuit.localeCurrencySymbol}${_.toNumber(giftCardSale.customMaximumAmount).toFixed(2)}`
        return `${customMinimumAmount} - ${customMaximumAmount}`
      }

      return `${this.currentCircuit.localeCurrencySymbol}${_.toNumber(giftCardSale.amount).toFixed(2)}`
    },
    formatImage (giftCardSale) {
      if (!this.section.config.giftCardSalesShowImage) return null

      const image = giftCardSale[this.localizeKey('image')] || giftCardSale.image || this.currentSite.giftCardImage

      if (_.isEmpty(image)) return null

      return giftCardSaleImageUrl(image, giftCardSale.color)
    },
    setBackgroundColor (colorName, customColorHex) {
      this.section.config.giftCardWidgetBackgroundColor = colorName
      this.section.config.giftCardWidgetBackgroundColorHex = customColorHex
    },
    setTextColor (colorName, customColorHex) {
      this.section.config.giftCardWidgetTextColor = colorName
      this.section.config.giftCardWidgetTextColorHex = customColorHex
    },
    setColor (colorName, customColorHex) {
      this.section.config.blockBackgroundColor = colorName
      this.section.config.blockBackgroundColorHex = customColorHex
    }
  }
}
</script>
